import { GetTableDto, TableFilter } from '@/services/table/table-filters';
import { SortingState } from '@tanstack/react-table';

import { TableStatus } from '@/common/types/phenomTypes';

export enum TableFilterActionType {
  SET_FILTERS = 'SET_FILTERS',
  SET_SEARCH = 'SET_SEARCH',
  SET_NAME = 'SET_NAME',
  SET_VARIANTS = 'SET_VARIANTS',
  SET_MIN_BUYIN = 'SET_MIN_BUYIN',
  SET_MAX_BUYIN = 'SET_MAX_BUYIN',
  SET_MIN_STAKE = 'SET_MIN_STAKE',
  SET_MAX_STAKE = 'SET_MAX_STAKE',
  SET_MAX_PLAYERS = 'SET_MAX_PLAYERS',
  SET_SEAT_COUNT = 'SET_SEAT_COUNT',
  SET_WAITLIST = 'SET_WAITLIST',
  SET_STATUS = 'SET_STATUS',
  SET_IS_PUBLIC = 'SET_IS_PUBLIC',
  SET_PAGINATION = 'SET_PAGINATION',
  SET_SORTING = 'SET_SORTING',
  RESET_FILTERS = 'RESET_FILTERS',
}

export type TableFilterAction =
  | { type: TableFilterActionType.SET_FILTERS; payload: TableFilter }
  | { type: TableFilterActionType.SET_SEARCH; payload: string }
  | { type: TableFilterActionType.SET_NAME; payload: string }
  | { type: TableFilterActionType.SET_VARIANTS; payload: string[] }
  | { type: TableFilterActionType.SET_MIN_BUYIN; payload: number }
  | { type: TableFilterActionType.SET_MAX_BUYIN; payload: number }
  | { type: TableFilterActionType.SET_MIN_STAKE; payload: number }
  | { type: TableFilterActionType.SET_MAX_STAKE; payload: number }
  | { type: TableFilterActionType.SET_MAX_PLAYERS; payload: number }
  | { type: TableFilterActionType.SET_SEAT_COUNT; payload: number }
  | { type: TableFilterActionType.SET_WAITLIST; payload: number }
  | { type: TableFilterActionType.SET_STATUS; payload: TableStatus }
  | { type: TableFilterActionType.SET_IS_PUBLIC; payload: boolean }
  | { type: TableFilterActionType.SET_PAGINATION; payload: { skip: number; take: number } }
  | { type: TableFilterActionType.SET_SORTING; payload: SortingState }
  | { type: TableFilterActionType.RESET_FILTERS };

export interface TableQueryState {
  filter: TableFilter;
  skip: number;
  take: number;
  sorting: SortingState;
}

export const initialFilters: TableFilter = {
  name: undefined,
  variants: undefined,
  minBuyin: undefined,
  maxBuyin: undefined,
  minStake: undefined,
  maxStake: undefined,
  maxPlayers: undefined,
  seatCount: undefined,
  waitlist: undefined,
  status: undefined,
  isPublic: undefined,
};

export const initialState: TableQueryState = {
  filter: initialFilters,
  skip: 0,
  take: 25,
  sorting: [
    {
      id: 'updatedAt',
      desc: true,
    },
  ],
};

export const tableQueryReducer = (state: TableQueryState, action: TableFilterAction): TableQueryState => {
  switch (action.type) {
    case TableFilterActionType.SET_FILTERS:
      return {
        ...state,
        filter: action.payload,
        skip: 0,
      };
    case TableFilterActionType.SET_SEARCH:
    case TableFilterActionType.SET_NAME:
      return {
        ...state,
        filter: { ...state.filter, name: action.payload },
        skip: 0,
      };
    case TableFilterActionType.SET_VARIANTS:
      return {
        ...state,
        filter: { ...state.filter, variants: action.payload },
        skip: 0,
      };
    case TableFilterActionType.SET_MIN_BUYIN:
      return {
        ...state,
        filter: { ...state.filter, minBuyin: action.payload },
        skip: 0,
      };
    case TableFilterActionType.SET_MAX_BUYIN:
      return {
        ...state,
        filter: { ...state.filter, maxBuyin: action.payload },
        skip: 0,
      };
    case TableFilterActionType.SET_MIN_STAKE:
      return {
        ...state,
        filter: { ...state.filter, minStake: action.payload },
        skip: 0,
      };
    case TableFilterActionType.SET_MAX_STAKE:
      return {
        ...state,
        filter: { ...state.filter, maxStake: action.payload },
        skip: 0,
      };
    case TableFilterActionType.SET_MAX_PLAYERS:
      return {
        ...state,
        filter: { ...state.filter, maxPlayers: action.payload },
        skip: 0,
      };
    case TableFilterActionType.SET_SEAT_COUNT:
      return {
        ...state,
        filter: { ...state.filter, seatCount: action.payload },
        skip: 0,
      };
    case TableFilterActionType.SET_WAITLIST:
      return {
        ...state,
        filter: { ...state.filter, waitlist: action.payload },
        skip: 0,
      };
    case TableFilterActionType.SET_STATUS:
      return {
        ...state,
        filter: { ...state.filter, status: action.payload },
        skip: 0,
      };
    case TableFilterActionType.SET_IS_PUBLIC:
      return {
        ...state,
        filter: { ...state.filter, isPublic: action.payload },
        skip: 0,
      };
    case TableFilterActionType.SET_PAGINATION:
      return {
        ...state,
        skip: action.payload.skip,
        take: action.payload.take,
      };
    case TableFilterActionType.SET_SORTING:
      return {
        ...state,
        sorting: action.payload,
        skip: 0,
      };
    case TableFilterActionType.RESET_FILTERS:
      return initialState;
    default:
      return state;
  }
};
